import { useState, useEffect, useMemo } from 'react';
import { DataTable, Column } from '@/components/DataTable';
import { Tabs, Tab } from '@/components/Tabs';
import { QuickViewPanel } from '@/components/QuickViewPanel';
import { Search } from 'lucide-react';

// Product interface matching architecture and story requirements
interface MarketplaceProduct {
  id: string;
  name: string;
  type: 'offer' | 'request';
  quantity: number; // in MT
  pricePerUnit: number; // in USD
  total: number; // in USD
  category?: string;
  commodityType?: string;
  origin?: string;
  regions?: string[];
  verified?: boolean;
  image?: string;
}

// Mock product data with variety of commodities
const mockProducts: MarketplaceProduct[] = [
  { id: '1', name: 'Brent Crude FOB', type: 'offer', quantity: 100, pricePerUnit: 85.50, total: 8550, category: 'Crude Oil', commodityType: 'Oil', origin: 'North Sea', regions: ['Europe', 'Middle East'], verified: true },
  { id: '2', name: 'Diesel Grade A', type: 'offer', quantity: 50, pricePerUnit: 92.00, total: 4600, category: 'Refined Products', commodityType: 'Oil', origin: 'USA', regions: ['North America'], verified: true },
  { id: '3', name: 'WTI Crude', type: 'offer', quantity: 200, pricePerUnit: 84.00, total: 16800, category: 'Crude Oil', commodityType: 'Oil', origin: 'USA', regions: ['North America'], verified: false },
  { id: '4', name: 'Gasoline Premium', type: 'request', quantity: 75, pricePerUnit: 88.50, total: 6637.50, category: 'Refined Products', commodityType: 'Oil', origin: 'Europe', regions: ['Europe'], verified: true },
  { id: '5', name: 'Heating Oil', type: 'offer', quantity: 120, pricePerUnit: 90.25, total: 10830, category: 'Refined Products', commodityType: 'Oil', origin: 'USA', regions: ['North America'], verified: false },
  { id: '6', name: 'Copper Ingots', type: 'offer', quantity: 500, pricePerUnit: 8.50, total: 4250, category: 'Metals', commodityType: 'Metal', origin: 'Chile', regions: ['South America'], verified: true },
  { id: '7', name: 'Wheat Grade A', type: 'request', quantity: 1000, pricePerUnit: 6.25, total: 6250, category: 'Grains', commodityType: 'Agricultural', origin: 'Ukraine', regions: ['Europe', 'Asia'], verified: false },
  { id: '8', name: 'Aluminum Sheets', type: 'offer', quantity: 300, pricePerUnit: 2.15, total: 645, category: 'Metals', commodityType: 'Metal', origin: 'China', regions: ['Asia'], verified: true },
  { id: '9', name: 'Corn Feed Grade', type: 'request', quantity: 800, pricePerUnit: 5.75, total: 4600, category: 'Grains', commodityType: 'Agricultural', origin: 'USA', regions: ['North America'], verified: false },
  { id: '10', name: 'Natural Gas LNG', type: 'offer', quantity: 150, pricePerUnit: 12.50, total: 1875, category: 'Energy', commodityType: 'Energy', origin: 'Qatar', regions: ['Middle East', 'Asia'], verified: true },
];

/**
 * MarketplacePage component - Main marketplace listing page
 * 
 * Features:
 * - Page title: "Marketplace"
 * - Tabs: All / Offers / Requests
 * - Search bar with debouncing (300ms)
 * - Data table with columns: Product, Quantity (MT), Price per unit, Total, View
 * - Tab filtering (All shows all, Offers shows only offers, Requests shows only requests)
 * - Search filtering (case-insensitive, real-time)
 * - Combined filtering (tab + search work together)
 * - Empty state: "No products found"
 * - Responsive design (table scrolls horizontally on mobile/tablet)
 * - Quick view panel: Desktop layout uses flex container with table and panel side-by-side
 *   - Panel height matches table container height
 *   - Panel positioned inline (not overlay), allows clicking different products
 */
export const MarketplacePage = () => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<MarketplaceProduct | null>(null);

  // Tabs configuration
  const tabs: Tab[] = [
    { id: 'all', label: 'All' },
    { id: 'offers', label: 'Offers' },
    { id: 'requests', label: 'Requests' },
  ];

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter products based on tab and search (combined filtering)
  const filteredProducts = useMemo(() => {
    let filtered = mockProducts;

    // Apply tab filter
    if (activeTab === 'offers') {
      filtered = filtered.filter(product => product.type === 'offer');
    } else if (activeTab === 'requests') {
      filtered = filtered.filter(product => product.type === 'request');
    }
    // 'all' tab shows all products (no filter)

    // Apply search filter (case-insensitive)
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeTab, debouncedSearchQuery]);

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle View button click - opens QuickViewPanel
  const handleViewClick = (product: MarketplaceProduct) => {
    setSelectedProduct(product);
    setIsPanelOpen(true);
  };

  // Handle panel close
  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setSelectedProduct(null);
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format quantity with MT unit
  const formatQuantity = (quantity: number): string => {
    return `${quantity} MT`;
  };

  // Table columns configuration
  const columns: Column<MarketplaceProduct>[] = [
    { id: 'name', label: 'Product' },
    {
      id: 'quantity',
      label: 'Quantity (MT)',
      render: (row) => formatQuantity(row.quantity),
    },
    {
      id: 'pricePerUnit',
      label: 'Price per unit',
      render: (row) => formatCurrency(row.pricePerUnit),
    },
    {
      id: 'total',
      label: 'Total',
      render: (row) => formatCurrency(row.total),
    },
    {
      id: 'view',
      label: 'View',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewClick(row);
          }}
          className="px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark border border-primary rounded transition-colors"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background w-full">
      {/* Page Title and Search Bar */}
      <div className="mb-4 flex items-center justify-between gap-6">
        <h1 className="text-h1 font-bold text-text-primary">Marketplace</h1>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search products in marketplace"
            className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base text-text-primary bg-background"
          />
        </div>
      </div>

      {/* Page Description */}
      <p className="mb-8 text-base text-text-secondary">Browse listings, place offers & submit bids</p>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* Data Table and Quick View Panel Container */}
      <div className="relative flex gap-6 w-full">
        {/* Data Table */}
        <div className={`bg-background border border-border rounded-lg overflow-hidden transition-all duration-300 ${isPanelOpen ? 'flex-1 min-w-0' : 'flex-1'}`}>
          <div className="p-6">
            <DataTable
              columns={columns}
              data={filteredProducts}
              emptyMessage="No products found"
              onRowClick={handleViewClick}
              selectedRowId={selectedProduct?.id}
            />
          </div>
        </div>

        {/* Quick View Panel */}
        {isPanelOpen && (
          <QuickViewPanel
            isOpen={isPanelOpen}
            onClose={handlePanelClose}
            product={selectedProduct}
          />
        )}
      </div>
    </div>
  );
};

